import { getPackageSalesforceId } from '~/components/Vendors/Profile/SetUpFlashOffer/PackageSetupStep/utils';

import type { OpportunityRelatedDeal, OpportunityRelatedInclusion } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { getOccupancyPaxFromText } from '../../../shared/occupancyUtils';
import type { ChecklistStatus } from '../../shared/checklistUtils';
import { isFiniteNumber } from '../../shared/checklistUtils';
import { getStatusLabel } from '../../shared/format';

import type {
  DealOptionApprovalChecklistRow,
  DealOptionsInclusionsChecklistRow,
  DealOptionsInclusionsDealRow,
} from './types';

const FLASH_FLEXI_CAMPAIGN_PATTERN = /flexi/i;

// Mirrors Salesforce's Deal_Offer__c.Extra_Night_Margin_Check__c formula (unsynced — see docs/business-rules.md § Flexi Nights Review Flag).
const EXTRA_NIGHT_MARGIN_GREEN_THRESHOLD = 20;

export function buildDealOptionsInclusionsRows({
  dealDataAvailable,
  relatedDeals,
  relatedInclusions,
  relatedInclusionsAvailable,
}: {
  dealDataAvailable: boolean;
  relatedDeals: Array<OpportunityRelatedDeal>;
  relatedInclusions: Array<OpportunityRelatedInclusion>;
  relatedInclusionsAvailable: boolean;
}): Array<DealOptionsInclusionsChecklistRow> {
  const rows: Array<DealOptionsInclusionsChecklistRow> = [];

  const inclusionsByDealSfid = new Map<string, Array<OpportunityRelatedInclusion>>();
  for (const inc of relatedInclusions) {
    const key = inc.deal_offer__c;
    if (!key) {
      continue;
    }
    const existing = inclusionsByDealSfid.get(key) ?? [];
    existing.push(inc);
    inclusionsByDealSfid.set(key, existing);
  }

  for (const deal of relatedDeals) {
    const dealKey = deal.sfid ?? String(deal.id);
    const roomName = deal.room_asset?.name ?? '—';

    if (!dealDataAvailable) {
      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: '',
        id: `blocked:deal_offer__c:${dealKey}`,
        inclusionAssetValue: null,
        dealOptionInclusionRrp: null,
        name: deal.name ?? '',
        rrpMatch: false,
        rrpOccupancyMismatchWith: [],
        roomName,
        status: 'blocked',
        statusLabel: getStatusLabel('blocked'),
        type: '',
      });
      continue;
    }

    if (!relatedInclusionsAvailable) {
      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: '',
        id: `blocked_inclusion:deal_offer__c:${dealKey}`,
        inclusionAssetValue: null,
        dealOptionInclusionRrp: null,
        name: deal.name ?? '',
        rrpMatch: false,
        rrpOccupancyMismatchWith: [],
        roomName,
        status: 'blocked',
        statusLabel: getStatusLabel('blocked'),
        type: '',
      });
      continue;
    }

    const nights = isFiniteNumber(deal.number_of_nights__c) ? deal.number_of_nights__c : null;

    for (const inc of inclusionsByDealSfid.get(dealKey) ?? []) {
      const rrp = isFiniteNumber(inc.rrp__c) ? inc.rrp__c : null;
      const assetValue = isFiniteNumber(inc.total_value__c) ? inc.total_value__c : null;
      const isDaily = normalizeInclusionType(inc.type__c) === 'daily';
      const expectedValue = isDaily && rrp !== null && nights !== null ? rrp * nights : rrp;
      const rrpMatch = expectedValue !== null && assetValue !== null && expectedValue === assetValue;

      rows.push({
        dealId: dealKey,
        dealOptionName: deal.name ?? '',
        description: inc.inclusion_description__c ?? inc.description__c ?? '',
        id: `inclusion:deal_offer__c:${dealKey}:inclusion__c:${inc.id}`,
        inclusionAssetValue: assetValue,
        dealOptionInclusionRrp: rrp,
        name: inc.name ?? '',
        rrpMatch,
        rrpOccupancyMismatchWith: [],
        roomName,
        status: rrpMatch ? 'approved' : 'requires approval',
        statusLabel: getStatusLabel(rrpMatch ? 'approved' : 'requires approval'),
        type: inc.type__c ?? '',
      });
    }
  }

  applyRrpOccupancyConsistencyCheck(rows, relatedDeals);

  return rows;
}

function applyRrpOccupancyConsistencyCheck(
  rows: Array<DealOptionsInclusionsChecklistRow>,
  relatedDeals: Array<OpportunityRelatedDeal>,
) {
  const occupancyByDealId = new Map(
    relatedDeals.map((deal) => [deal.sfid ?? String(deal.id), getOccupancyPaxFromText(deal.name)]),
  );

  const rowsByInclusionName = new Map<string, Array<DealOptionsInclusionsChecklistRow>>();
  for (const row of rows) {
    if (row.status === 'blocked' || !row.name) {
      continue;
    }
    const key = row.name.trim().toLowerCase();
    const existing = rowsByInclusionName.get(key) ?? [];
    existing.push(row);
    rowsByInclusionName.set(key, existing);
  }

  for (const groupRows of rowsByInclusionName.values()) {
    for (let i = 0; i < groupRows.length; i++) {
      for (let j = i + 1; j < groupRows.length; j++) {
        const rowA = groupRows[i];
        const rowB = groupRows[j];
        const occupancyA = occupancyByDealId.get(rowA.dealId);
        const occupancyB = occupancyByDealId.get(rowB.dealId);
        // Per-stay inclusions and a 0 RRP aren't expected to scale with occupancy, so an
        // identical RRP across deal options isn't a sign the inclusion was left unscaled.
        const isPerStay = normalizeInclusionType(rowA.type) === 'per stay';
        const isRrpUnexpectedlyIdentical =
          isFiniteNumber(occupancyA) &&
          isFiniteNumber(occupancyB) &&
          occupancyA !== occupancyB &&
          isFiniteNumber(rowA.dealOptionInclusionRrp) &&
          rowA.dealOptionInclusionRrp !== 0 &&
          !isPerStay &&
          rowA.dealOptionInclusionRrp === rowB.dealOptionInclusionRrp;

        if (!isRrpUnexpectedlyIdentical) {
          continue;
        }

        rowA.rrpOccupancyMismatchWith.push(rowB.dealOptionName);
        rowB.rrpOccupancyMismatchWith.push(rowA.dealOptionName);
      }
    }
  }

  // Approval requires both signals to be clean — they're surfaced as separate columns, but
  // either one being wrong means the row still needs a CM's eyes on it.
  for (const row of rowsByInclusionName.values()) {
    for (const inclusionRow of row) {
      const approved = inclusionRow.rrpMatch && inclusionRow.rrpOccupancyMismatchWith.length === 0;
      inclusionRow.status = approved ? 'approved' : 'requires approval';
      inclusionRow.statusLabel = getStatusLabel(inclusionRow.status);
    }
  }
}

export function buildDealOptionsInclusionsDealRows({
  campaignType,
  flatRows,
  packages,
  relatedDeals,
}: {
  campaignType?: string | null;
  flatRows: Array<DealOptionsInclusionsChecklistRow>;
  packages?: Array<App.Package>;
  relatedDeals: Array<OpportunityRelatedDeal>;
}): Array<DealOptionsInclusionsDealRow> {
  const inclusionsByDealId = new Map<string, Array<DealOptionsInclusionsChecklistRow>>();
  for (const row of flatRows) {
    const existing = inclusionsByDealId.get(row.dealId) ?? [];
    existing.push(row);
    inclusionsByDealId.set(row.dealId, existing);
  }

  const packagesBySalesforceId = new Map(
    (packages ?? []).flatMap((pkg) => {
      const salesforceId = getPackageSalesforceId(pkg);
      return salesforceId ? [[salesforceId, pkg] as const] : [];
    }),
  );

  const isFlashFlexiCampaign = !!campaignType && FLASH_FLEXI_CAMPAIGN_PATTERN.test(campaignType);

  return relatedDeals.map((deal) => {
    const dealKey = deal.sfid ?? String(deal.id);
    const matchedPackage = packagesBySalesforceId.get(dealKey);
    const adminFlexibleNights = !!(
      matchedPackage &&
      'flexible_nights' in matchedPackage &&
      matchedPackage.flexible_nights
    );
    const maxExtraNights = isFiniteNumber(deal.max_extra_nights__c)
      ? deal.max_extra_nights__c
      : Number(deal.max_extra_nights__c ?? 0) || 0;
    const extraNightMarginGreen =
      isFiniteNumber(deal.extra_night_margin__c) && deal.extra_night_margin__c >= EXTRA_NIGHT_MARGIN_GREEN_THRESHOLD;
    const hasSupportingFlexiSignal = maxExtraNights > 0 || isFlashFlexiCampaign || extraNightMarginGreen;
    const needsFlexiNightsReview = !adminFlexibleNights && hasSupportingFlexiSignal;
    const status: ChecklistStatus = needsFlexiNightsReview ? 'requires approval' : 'approved';

    return {
      id: dealKey,
      costPrice: deal.cost_price__c ?? null,
      currency: deal.currencyisocode ?? 'AUD',
      dealOptionName: deal.name ?? '',
      inclusions: inclusionsByDealId.get(dealKey) ?? [],
      marginPercent: deal.margin_percent__c ?? null,
      needsFlexiNightsReview,
      rackRate: deal.room_asset?.rack_rate__c ?? null,
      roomAssetLinked: !!(deal.room_asset?.id ?? deal.room_asset?.sfid),
      roomName: deal.room_asset?.name ?? '—',
      sellPrice: deal.price__c ?? null,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

// Gated by the flexi-nights review flag.
export function buildDealOptionApprovalRows(
  dealRows: Array<DealOptionsInclusionsDealRow>,
): Array<DealOptionApprovalChecklistRow> {
  return dealRows.map((deal) => ({ id: deal.id, status: deal.status, statusLabel: deal.statusLabel }));
}

function normalizeInclusionType(type?: string | null) {
  return type?.trim().toLowerCase() ?? '';
}

export function getDealOptionsInclusionsSearchText(row: DealOptionsInclusionsChecklistRow) {
  return [row.dealOptionName, row.name, row.description, row.type, row.roomName, row.statusLabel]
    .filter(Boolean)
    .join(' ');
}

export function getDealSearchText(deal: DealOptionsInclusionsDealRow) {
  return [deal.dealOptionName, deal.roomName, deal.statusLabel].filter(Boolean).join(' ');
}
